import { EnvelopeIcon, EyeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { Button } from '@heroui/react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import GenericAlert, { type GenericAlertProps } from '@/app/components/generic-alert.tsx';
import { NoData } from '@/app/components/no-data.tsx';
import { OnboardImportStatusIndicator } from '@/app/components/onboarding/onboard-import-status-indicator.tsx';
import OnboardJsonLineItemDialog from '@/app/components/onboarding/onboard-json-line-item-dialog.tsx';
import { usePageLoading } from '@/app/components/page-loading.tsx';
import { Paginator, usePagination } from '@/app/components/paginator.tsx';
import { formatDate } from '@/app/helpers/date.helpers.ts';
import toast from '@/app/toast.ts';
import { Field } from '@/primitives/fieldset.tsx';
import { Input, InputGroup } from '@/primitives/input.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useSort,
} from '@/primitives/table.tsx';
import { TextLink } from '@/primitives/text.tsx';
import { usePostOnboardImportSendRepEmailsMutation } from '@/slices/onboard-import-api-slice.ts';
import { useFilter } from '../../hooks/use-filter.ts';
import type { OnboardImportRepLineItemModel } from '../../types/onboard-import.type.ts';

const RepImportTable = ({ lineItems }: { lineItems: OnboardImportRepLineItemModel[] }) => {
  const [lineOpen, setLineOpen] = useState(false);
  const [lineItem, setLineItem] = useState(null);
  const [sendRepEmail, { isLoading, isError, isSuccess }] =
    usePostOnboardImportSendRepEmailsMutation();
  const memoizedSortingDataAccessor = useCallback(
    (value: OnboardImportRepLineItemModel, column: string) => {
      switch (column) {
        case 'status':
          return _(value.onboardImportStatuses)
            .map(x => x.onboardImportStatusType.id)
            .max();
        case 'representative':
          return (
            value.accountRepresentative?.representativeLastName ??
            value.rxVantageJsonLineItem?.representative ??
            value.jsonLineItem?.lastName
          );
        case 'emailSentAt':
          return DateTime.fromISO(value[column]).isValid ? DateTime.fromISO(value[column]) : null;
        default:
          return _.get(value, column);
      }
    },
    [],
  );

  const { sort, setSort, sortData } = useSort({
    data: lineItems,
    memoizedSortingDataAccessor,
  });
  const { filter, setFilter, filteredData } = useFilter({ data: sortData });
  const paginator = usePagination({ data: filteredData });
  const { pagedData } = paginator;
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertProps, setAlertProps] = useState<GenericAlertProps>(null);

  useEffect(() => {
    if (isSuccess) {
      toast.success('Email(s) sent');
    }

    if (isError) {
      toast.error('There was an error attempting to send the email(s)');
    }
  }, [isError, isSuccess]);

  const { showPageLoading, hidePageLoading } = usePageLoading();

  useEffect(() => {
    isLoading ? showPageLoading('rep-email-loading') : hidePageLoading('rep-email-loading');
  }, [isLoading]);

  const sendWelcomeEmail = (values: OnboardImportRepLineItemModel[]) => {
    const emails = values.filter(x => x.accountRepresentative);
    const props = {
      message: `Are you sure you want to send welcome emails to ${emails.length} rep(s)?`,
      onConfirm: () => {
        setAlertOpen(false);
        sendRepEmail(emails);
      },
    } as GenericAlertProps;
    setAlertProps(props);
    setAlertOpen(true);
  };

  return (
    <>
      <div className='flex justify-between pb-5'>
        <Field className='lg:w-1/6'>
          <InputGroup>
            <Input
              type='text'
              onInput={(event: any) => setFilter(event.target.value)}
              value={filter}
              placeholder='Filter text'
            />
            <MagnifyingGlassIcon />
          </InputGroup>
        </Field>
        <Button
          color='primary'
          variant='solid'
          onClick={() => sendWelcomeEmail(lineItems)}
          title='Send to all imported reps'
        >
          <EnvelopeIcon />
          Send Welcome Emails
        </Button>
      </div>

      <Table currentSort={sort} setCurrentSort={setSort}>
        <TableHead>
          <TableRow>
            <TableHeader sortColumn='status'>Status</TableHeader>
            <TableHeader sortColumn='rowNumber'>Row #</TableHeader>
            <TableHeader sortColumn='representative'>Rep</TableHeader>
            <TableHeader sortColumn='emailSentAt'>Email Sent</TableHeader>
            <TableHeader>Notes</TableHeader>
            <TableHeader></TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedData.length ? (
            pagedData.map(value => (
              <TableRow key={value.id}>
                <TableCell>
                  <OnboardImportStatusIndicator
                    status={
                      _(value.onboardImportStatuses)
                        .orderBy(x => x.onboardImportStatusType.id, 'desc')
                        .head()?.onboardImportStatusType
                    }
                  />
                </TableCell>
                <TableCell>{value.rowNumber}</TableCell>
                <TableCell>
                  {value.accountRepresentative?.representativeId > 0 && (
                    <TextLink
                      href={`/rep-public-profile/${value.accountRepresentative.representativeKey}`}
                    >
                      {value.accountRepresentative.representativeFirstName}{' '}
                      {value.accountRepresentative.representativeLastName}
                    </TextLink>
                  )}
                  {!value.accountRepresentative?.representativeId && value.jsonLineItem && (
                    <>
                      {value.jsonLineItem.firstName} {value.jsonLineItem.lastName} (
                      {value.jsonLineItem.emailAddress || 'No email'})
                    </>
                  )}
                  {!value.accountRepresentative?.representativeId &&
                    value.rxVantageJsonLineItem &&
                    value.rxVantageJsonLineItem.representative}
                </TableCell>
                <TableCell>{formatDate(value.emailSentAt, DateTime.DATETIME_SHORT, '')}</TableCell>
                <TableCell>{value.onboardImportStatuses.map(x => x.notes).join(' ')}</TableCell>
                <TableCell>
                  <Button
                    variant='light'
                    onClick={() => {
                      setLineItem(value.rxVantageJsonLineItem ?? value.jsonLineItem);
                      setLineOpen(true);
                    }}
                    title='View line item'
                    isIconOnly
                  >
                    <EyeIcon />
                  </Button>
                  {value.accountRepresentative ? (
                    <Button
                      variant='light'
                      onClick={() => sendWelcomeEmail([value])}
                      title='Send welcome email'
                      isIconOnly
                    >
                      <EnvelopeIcon />
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5}>
                <NoData />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Paginator {...paginator} />
      <OnboardJsonLineItemDialog
        isOpen={lineOpen}
        setOpen={setLineOpen}
        jsonLineItem={lineItem}
        title={'Rep Line Item'}
      />
      <GenericAlert
        isOpen={alertOpen}
        message={alertProps?.message}
        setIsOpen={setAlertOpen}
        title='Send Representative Welcome Email Confirm'
        onConfirm={alertProps?.onConfirm}
      />
    </>
  );
};

export default RepImportTable;
